import BigNumber from 'bignumber.js';
var bigDecimal = require('bigdecimal');
// import bigDecimal from 'bigdecimal.js';

type IBigNumberArg = string | number | BigNumber;

export const formatRoundFloorDisplay = (
  value: IBigNumberArg,
  decimalPlace = 4,
  shiftedBy = 0,
): string => {
  return new BigNumber(value || 0)
    .shiftedBy(-shiftedBy)
    .decimalPlaces(decimalPlace, BigNumber.ROUND_FLOOR)
    .toFormat();
};

export const formatRoundFloorDisplayWithPrecision = (
  value: IBigNumberArg,
  decimalPlace = 4,
  shiftedBy = 0,
  decimalCount = 0,
): string => {
  return new BigNumber(value || 0)
    .shiftedBy(-shiftedBy)
    .decimalPlaces(decimalPlace, BigNumber.ROUND_FLOOR)
    .toFormat(decimalCount);
};

export const formatRoundFloorDisplayOrderBook = (
  value: string | number | BigNumber,
  decimalPlace = 4,
  shiftedBy = 0,
): string => {
  return new BigNumber(value || 0)
    .shiftedBy(-shiftedBy)
    .decimalPlaces(decimalPlace, BigNumber.ROUND_FLOOR)
    .toFormat(decimalPlace);
};

function roundPriceOrderBook(value: any, precision: any = 10, roundingMode: any) {
  if (value % precision !== 0) {
    const decimal = new bigDecimal.BigDecimal(0.1 / precision);
    const scale = new bigDecimal.BigDecimal(10 * precision);
    value = new bigDecimal.BigDecimal(value)
      .multiply(decimal)
      .setScale(1, roundingMode)
      .multiply(scale);
  }
  return new BigNumber(value).toString() === '0' ? 1 : new BigNumber(value);
}

export const formatRoundUpOrderBookAsks = (
  value: string | number | BigNumber,
  decimalPlace = 4,
  shiftedBy = 0,
  precision: string | number,
): string => {
  if (Number(precision) >= 10) {
    return roundPriceOrderBook(
      value.toString(),
      precision,
      bigDecimal.BigDecimal.ROUND_UP,
    ).toString();
  } else {
    return new BigNumber(value || 0)
      .shiftedBy(-shiftedBy)
      .decimalPlaces(decimalPlace, BigNumber.ROUND_UP)
      .toFormat(decimalPlace);
  }
};

export const formatRoundDownOrderBookBids = (
  value: string | number | BigNumber,
  decimalPlace = 4,
  shiftedBy = 0,
  precision: string | number | BigNumber,
): string => {
  if (Number(precision) >= 10) {
    return roundPriceOrderBook(
      value.toString(),
      precision,
      bigDecimal.BigDecimal.ROUND_DOWN,
    ).toString();
  } else {
    return new BigNumber(value || 0)
      .shiftedBy(-shiftedBy)
      .decimalPlaces(decimalPlace, BigNumber.ROUND_DOWN)
      .toFormat(decimalPlace);
  }
};

export const formatRoundFloorDisplayWithCompare = (
  value: IBigNumberArg,
  decimalPlace = 4,
  shiftedBy = 0,
): string => {
  const data = new BigNumber(value || 0)
    .shiftedBy(-shiftedBy)
    .decimalPlaces(decimalPlace, BigNumber.ROUND_FLOOR)
    .toFormat()
    .toString();
  if (Number(value.toString()) !== 0 && new BigNumber(data).lt(0.01)) {
    return '<0.01';
  }
  return data;
};

export const formatRoundFloorDisplayWithCompareFixed = (
  value: IBigNumberArg,
  decimalPlace = 4,
  shiftedBy = 0,
): string => {
  const data = new BigNumber(value || 0).shiftedBy(-shiftedBy).toFixed(decimalPlace);
  if (Number(value.toString()) !== 0 && new BigNumber(data).lt(0.01)) {
    return '<0.01';
  }
  return data;
};

export const convertRoundFloor = (
  value: IBigNumberArg,
  decimalPlace = 4,
  shiftedBy = 0,
): string => {
  return new BigNumber(value || 0)
    .shiftedBy(-shiftedBy)
    .decimalPlaces(decimalPlace, BigNumber.ROUND_FLOOR)
    .toString();
};

export const nFormatter = (number: string, digits = 4, roundingMode?: BigNumber.RoundingMode) => {
  const SI = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
    { value: 1e12, symbol: 'T' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const num = parseFloat(number);
  let i;
  for (i = SI.length - 1; i > 0; i--) {
    if (num >= SI[i].value) {
      break;
    }
  }
  if (roundingMode) {
    return (
      new BigNumber(num)
        .div(SI[i].value)
        .toFixed(digits, roundingMode)
        .toString()
        .replace(rx, '$1') + SI[i].symbol
    );
  }

  return (num / SI[i].value).toFixed(digits).replace(rx, '$1') + SI[i].symbol;
};

export const nFormatterV2 = (
  number: string,
  digits = 4,
  roundingMode?: BigNumber.RoundingMode,
  symbol?: string, // Ex: "$", "₫", "€"
) => {
  const SI = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
    { value: 1e12, symbol: 'T' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const num = parseFloat(number);
  const absNum = Math.abs(num);

  let i;
  for (i = SI.length - 1; i > 0; i--) {
    if (absNum >= SI[i].value) {
      break;
    }
  }

  const baseValue = absNum / SI[i].value;

  const formatted = roundingMode
    ? new BigNumber(baseValue).toFixed(digits, roundingMode)
    : baseValue.toFixed(digits);

  const cleaned = formatted.replace(rx, '$1') + SI[i].symbol;

  const result = (symbol ?? '') + cleaned;

  return num < 0 ? '-' + result : result;
};

export const formatFixedPoint = (number: IBigNumberArg, dp: number) => {
  return new BigNumber(number).toFixed(dp);
};

export const roundDownToDecimals = (value: number, decimals: number): number => {
  const factor = Math.pow(10, decimals);
  return Math.floor(value * factor) / factor;
};

export const format6SignificantFigures = (
  value: IBigNumberArg,
  shiftedBy = 0,
): string => {
  if (!value && value !== 0) return '0.00000';

  const bigNumberValue = new BigNumber(value).shiftedBy(-shiftedBy);

  if (bigNumberValue.isZero()) {
    return '0.00000';
  }

  const precisionResult = bigNumberValue.toPrecision(6, BigNumber.ROUND_DOWN);

  const finalNumber = new BigNumber(precisionResult);

  if (finalNumber.isGreaterThanOrEqualTo(0.000001) && finalNumber.isLessThan(1000000)) {
    return finalNumber.toNumber().toLocaleString('en-US', {
      maximumFractionDigits: 10,
      minimumFractionDigits: 0,
    });
  }

  return finalNumber.toNumber().toLocaleString('en-US', {
    maximumFractionDigits: 10,
    minimumFractionDigits: 0,
  });
};


/**
 * Formats small decimal prices to avoid showing as 0 while preserving standard formatting for larger numbers
 * Uses 6 significant figures for very small numbers (< 0.001) and standard toLocaleString for others
 * 
 * @param price - The price number to format
 * @returns Formatted price string that avoids scientific notation for small decimals
 * 
 * @example
 * formatSmallDecimalPrice(0.0000112) // "0.0000112"
 * formatSmallDecimalPrice(0.00001119) // "0.00001119" 
 * formatSmallDecimalPrice(50000) // "50,000"
 * formatSmallDecimalPrice(1.5) // "1.5"
 */
export const formatSmallDecimalPrice = (price: number): string => {
  // For very small numbers, use 6 significant figures to avoid showing as 0
  if (price < 0.001 && price > 0) {
    const bigNumberValue = new BigNumber(price);

    if (bigNumberValue.isZero()) {
      return '0';
    }

    // For extremely small numbers, use fixed decimal notation to avoid scientific notation
    if (price < 0.000001) {
      const precisionResult = bigNumberValue.toPrecision(6, BigNumber.ROUND_DOWN);
      const finalNumber = new BigNumber(precisionResult);
      return finalNumber.toFixed();
    }

    // For small but not extremely small numbers, use 6 significant figures
    const precisionResult = bigNumberValue.toPrecision(6, BigNumber.ROUND_DOWN);
    const finalNumber = new BigNumber(precisionResult);
    return finalNumber.toString();
  }

  // For regular numbers, use standard locale formatting
  return price.toLocaleString();
};